import API from "./axios";

const jobWorkEntryApi = {
  getAll:       ()          => API.get("/job-work"),

  // Phase 36da.99.wajw — WhatsApp the job-work statement to the payee.
  // Returns 200 even when it did NOT send (no mobile, not configured, not
  // CONFIRMED) — read `sent` from the body, not the status code.
  sendWhatsApp: (id) => API.post(`/job-work/${id}/send-whatsapp`),

  // ── Phase 36da.99.jwrep — Job Work Report (mirrors the Fixed Salary Report) ──
  // Grouped into team-leader / self / not-assigned sections, with one amount
  // column per distinct entry period found inside the window.
  report:       (fromDate, toDate, scope = "ALL", scopeId, payeeType = "ALL") =>
    API.get(`/job-work/report?fromDate=${fromDate}&toDate=${toDate}&scope=${scope}&payeeType=${payeeType}${scopeId != null ? `&scopeId=${scopeId}` : ""}`),

  // Entities for the second ("which") dropdown, given the scope + window.
  reportScopeOptions: (fromDate, toDate, scope = "ALL", payeeType = "ALL") =>
    API.get(`/job-work/report/scope-options?fromDate=${fromDate}&toDate=${toDate}&scope=${scope}&payeeType=${payeeType}`),

  // rangeIds = the periods the user ticked; omitted means "all periods".
  reportPdf:    (fromDate, toDate, scope = "ALL", scopeId, rangeIds, payeeType = "ALL") =>
    API.get(`/job-work/report/pdf?fromDate=${fromDate}&toDate=${toDate}&scope=${scope}&payeeType=${payeeType}${scopeId != null ? `&scopeId=${scopeId}` : ""}${(rangeIds && rangeIds.length) ? rangeIds.map(r => `&rangeIds=${encodeURIComponent(r)}`).join("") : ""}`, { responseType: "blob" }),

  reportExcel:  (fromDate, toDate, scope = "ALL", scopeId, rangeIds, payeeType = "ALL") =>
    API.get(`/job-work/report/excel?fromDate=${fromDate}&toDate=${toDate}&scope=${scope}&payeeType=${payeeType}${scopeId != null ? `&scopeId=${scopeId}` : ""}${(rangeIds && rangeIds.length) ? rangeIds.map(r => `&rangeIds=${encodeURIComponent(r)}`).join("") : ""}`, { responseType: "blob" }),

  // Phase 5 — server-side pagination + sort + search.
  // params: { page, size, sortBy, sortDir, q, status, payeeType, employeeId, contractorId, fromDate, toDate, locationId }
  paged:        (params = {}) => API.get("/job-work/paged", { params }),
  // Confirmed job work not yet settled, grouped by employee. Feeds the
  // Payment Pending tab; each row carries the entry ids so a payment can
  // stamp exactly the entries it settled.
  paymentPending: (params) => API.get("/job-work/payment-pending", { params }),
  getById:      (id)        => API.get(`/job-work/${id}`),
  create:       (payload)   => API.post("/job-work", payload),
  // COMBINED BILL — several contractors on one bill. entries: [payload, …],
  // each exactly what create() takes; one entry per contractor, tied by a
  // group number ("JWG/26/00001").
  createCombined: (entries) => API.post("/job-work/combined", { entries }),
  group:        (number)    => API.get("/job-work/group", { params: { number } }),
  confirmGroup: (number)    => API.patch("/job-work/group/confirm", null, { params: { number } }),
  groupPdfUrl:  (number)    => `/job-work/group/pdf?number=${encodeURIComponent(number)}`,
  // Phase 36da.99.rebill — one-time admin: recreate Job Work Entries (+ ledger
  // credits) from confirmed unbilled inwards, for employees + contractors.
  rebillFromInwards: (from, to) => API.post(`/job-work/rebill-from-inwards${from || to ? `?${[from ? `from=${from}` : "", to ? `to=${to}` : ""].filter(Boolean).join("&")}` : ""}`),
  // Stitching → one DRAFT entry per employee with that employee's unbilled
  // stitching entry lines between from and to. Safe to run again.
  generateStitching: (from, to, entryDate) =>
    API.post("/job-work/generate-stitching", null, { params: { from, to, entryDate: entryDate || undefined } }),
  // Phase 36da.99.cc — edit a DRAFT JWE.  Backend rejects with 400 if
  // status != DRAFT.  Payload shape is identical to create.
  update:       (id, payload) => API.put(`/job-work/${id}`, payload),
  confirm:      (id)        => API.patch(`/job-work/${id}/confirm`),
  cancel:       (id)        => API.patch(`/job-work/${id}/cancel`),
  pdfUrl:       (id)        => `${API.defaults.baseURL}/job-work/${id}/pdf`,

  /**
   * Pending inwards + pending issues for the given payee + date range.
   * payeeType: 'EMPLOYEE' | 'CONTRACTOR'
   * employeeId | contractorId (one required)
   * from, to : ISO date strings yyyy-MM-dd
   * excludeJobWorkEntryId (optional) — set when the picker is open in
   *   edit mode so the JWE's own claim is subtracted from billed.
   * locationId (optional) — only work done at that location (an entry
   *   bills one location's work).
   */
  getPending: ({ payeeType, employeeId, contractorId, from, to, excludeJobWorkEntryId, locationId }) => {
    const params = new URLSearchParams();
    params.append("payeeType", payeeType);
    if (employeeId)   params.append("employeeId",   employeeId);
    if (contractorId) params.append("contractorId", contractorId);
    params.append("from", from);
    params.append("to",   to);
    if (excludeJobWorkEntryId) params.append("excludeJobWorkEntryId", excludeJobWorkEntryId);
    if (locationId)   params.append("locationId",   locationId);
    return API.get(`/job-work/pending?${params.toString()}`);
  },

  /**
   * Phase 36da.99.bp — outstanding per-type advance / loan / contribution
   * grid for the selected payee.  Returns 7 rows (ADVANCE / LOAN / PF /
   * ESI / MACHINERY / PURCHASE / PT) with totalAmount + suggestedDeduction.
   * The JWE form renders this as an editable grid + folds non-zero
   * deductions into the save payload.
   */
  getAdvanceOutstandings: ({ payeeType, employeeId, contractorId }) => {
    const params = new URLSearchParams();
    params.append("payeeType", payeeType);
    if (employeeId)   params.append("employeeId",   employeeId);
    if (contractorId) params.append("contractorId", contractorId);
    return API.get(`/job-work/advance-outstandings?${params.toString()}`);
  },

  /**
   * CONFIRMED but unpaid entries for a payee — used by PaymentPage to pick which entries to pay.
   */
  getUnpaid: ({ payeeType, employeeId, contractorId }) => {
    const params = new URLSearchParams();
    params.append("payeeType", payeeType);
    if (employeeId)   params.append("employeeId",   employeeId);
    if (contractorId) params.append("contractorId", contractorId);
    return API.get(`/job-work/unpaid?${params.toString()}`);
  },

  /** Open a PDF in a new tab with auth — fixes 403 from raw window.open (no JWT header). */
  openPdfUrl: async (url) => {
    const res = await API.get(url, { responseType: "blob" });
    // Phase 36da.99.jwfn — use the server's Content-Disposition filename
    // ({employee}_{date range}.pdf) so a saved file is named meaningfully
    // instead of a random blob UUID.
    const fname = parseFilename(res.headers?.["content-disposition"]) || "JobWork.pdf";
    openNamedPdf(res.data, fname);
  },

  /** Preview PDF from draft selection (no save). Opens in new tab. */
  previewPdf: async (payload) => {
    const res = await API.post("/job-work/preview-pdf", payload, { responseType: "blob" });
    const fname = parseFilename(res.headers?.["content-disposition"]) || "JobWork-Preview.pdf";
    openNamedPdf(res.data, fname);
  },
};

// Phase 36da.99.jwfn — pull filename="..." (or filename*=) out of a
// Content-Disposition header value.
function parseFilename(cd) {
  if (!cd) return null;
  let m = /filename\*=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(cd);
  if (m && m[1]) { try { return decodeURIComponent(m[1]); } catch { return m[1]; } }
  m = /filename=["']?([^"';]+)["']?/i.exec(cd);
  return m && m[1] ? m[1] : null;
}

// Open the PDF for viewing AND guarantee the saved file uses {name}_{dates}.pdf.
//
// Chrome's built-in PDF viewer names an in-tab "save" after the blob URL's
// random UUID, ignoring both the File name and Content-Disposition — so a
// preview-only open can never control the filename.  The only reliable way to
// name the file is an <a download="…"> click.  We therefore trigger a named
// download (correct filename, every browser) and also open a preview tab.
function openNamedPdf(data, filename) {
  const blob = new Blob([data], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);

  // Preview in a new tab (best-effort; ignored if a popup blocker stops it).
  // Set the tab's document title to the filename — some browsers use it as the
  // default "save as" name in the PDF viewer.
  try {
    const win = window.open(blobUrl, "_blank");
    if (win) {
      const t = filename.replace(/\.pdf$/i, "");
      const setTitle = () => { try { win.document.title = t; } catch (_) { /* cross-origin */ } };
      setTimeout(setTitle, 300);
      setTimeout(setTitle, 1200);
    }
  } catch (_) { /* ignore */ }

  // Named download — this is what makes the saved file {name}_{dates}.pdf.
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();

  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

export default jobWorkEntryApi;